import { dedent } from 'ts-dedent'
import {
  ColumnDataType,
  GetOutputSchemaParams,
  Provider,
  QueryAndOutputs,
  TableSchema,
  WorkflowCodeType,
  WorkflowComponentCodeParams,
  WorkflowComponentParamType
} from '../types'
import { getDBTypesByProvider } from '../utils/index'
import { addColumn, removeColumn } from '../utils/schemas'
import { SingleOutputWorkflowComponent } from './common/singleoutputcomponent'

class Cast extends SingleOutputWorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.cast',
        group: 'Data Preparation',
        title: 'Cast',
        description: `
      This component casts the content of a column to a given type
      `
      },
      [
        {
          name: 'source',
          type: WorkflowComponentParamType.InputTable,
          title: 'Source table',
          optional: true
        },
        {
          name: 'column',
          type: WorkflowComponentParamType.Column,
          title: 'Column',
          parent: 'source',
          dataType: [
            ColumnDataType.Number,
            ColumnDataType.String,
            ColumnDataType.Boolean,
            ColumnDataType.Date,
            ColumnDataType.Time,
            ColumnDataType.Datetime,
            ColumnDataType.Timestamp,
            ColumnDataType.Binary
          ],
          providers: [
            Provider.BigQuery,
            Provider.Snowflake,
            Provider.Redshift,
            Provider.Postgres
          ]
        },
        {
          name: 'databricks_column',
          type: WorkflowComponentParamType.Column,
          title: 'Column',
          parent: 'source',
          dataType: [
            ColumnDataType.Number,
            ColumnDataType.String,
            ColumnDataType.Boolean,
            ColumnDataType.Date,
            ColumnDataType.Time,
            ColumnDataType.Datetime,
            ColumnDataType.Timestamp
          ],
          providers: [Provider.DatabricksRest]
        },
        {
          name: 'newtype',
          type: WorkflowComponentParamType.SelectionType,
          title: 'New type',
          parent: 'column',
          providers: [
            Provider.BigQuery,
            Provider.Snowflake,
            Provider.Redshift,
            Provider.Postgres
          ]
        },
        {
          name: 'databricks_newtype',
          type: WorkflowComponentParamType.SelectionType,
          title: 'New type',
          parent: 'databricks_column',
          providers: [Provider.DatabricksRest]
        }
      ]
    )
  }

  async getCode({
    workflow,
    params,
    connection,
    componentName,
    codeType
  }: WorkflowComponentCodeParams): Promise<QueryAndOutputs | null> {
    const dryRun = codeType === WorkflowCodeType.DryRun
    const outputTablename = connection.getOutputTableName(
      workflow.id,
      componentName,
      params,
      'result',
      codeType
    )
    const preparedParams = this._prepareParameters(params)
    let query = ''
    const column =
      connection.type === Provider.DatabricksRest
        ? (preparedParams.databricks_column as string)
        : (preparedParams.column as string)
    const newtype =
      connection.type === Provider.DatabricksRest
        ? (preparedParams.databricks_newtype as string)
        : (preparedParams.newtype as string)
    switch (connection.type) {
      case Provider.BigQuery: {
        const select = `SELECT * REPLACE (SAFE_CAST(${column}
          AS ${newtype}) AS ${column})
          FROM ${connection.quoteName(preparedParams.source as string)}`
        query = connection.getCreateTempTableQuery(
          outputTablename,
          select,
          codeType,
          null
        )
        break
      }
      case Provider.Snowflake: {
        const select = `SELECT * EXCLUDE(${column}),
          CAST(${column} AS ${newtype}) AS ${column}
          FROM ${connection.quoteName(preparedParams.source as string)}`

        query = connection.getCreateTempTableQuery(
          outputTablename,
          select,
          codeType,
          null
        )
        break
      }
      case Provider.Postgres: {
        const select = `SELECT %s
          FROM ${connection.quoteName(preparedParams.source as string)}`
        const createTable = connection.getCreateTempTableQuery(
          outputTablename,
          select,
          codeType,
          null
        )
        const tokens = ((preparedParams.source as string) ?? '').split('.')
        const datasetname = tokens[0]
        const schemaname = tokens[1]
        const tablename = tokens[2]
        query = dedent`DECLARE
                  columns_def TEXT;
                BEGIN
                  EXECUTE '
                    WITH __alias AS(
                      SELECT column_name AS col_alias
                      FROM ${datasetname}.INFORMATION_SCHEMA.COLUMNS
                      WHERE table_name = ''${tablename}'' and
                        table_schema = ''${schemaname}'' and
                        table_catalog = ''${datasetname}'' and
                        column_name != ''${column}''
                    )
                    SELECT STRING_AGG(col_alias, '', '') aliases
                    FROM __alias
                  ' INTO columns_def;
                  IF columns_def IS NULL THEN
                    columns_def:= 'CAST(${column} AS ${newtype}) AS ${column}';
                  ELSE
                    columns_def:= columns_def || ', ' || 'CAST(${column} AS ${newtype}) AS ${column}';
                  END IF;
                  EXECUTE
                    REPLACE(
                      '${createTable}',
                      '%s',
                      columns_def
                      );
                END;`
        break
      }
      case Provider.Redshift: {
        const tempselect = connection.getTempSelectQuery(
          `SELECT %s
          FROM ${connection.quoteName(preparedParams.source as string)}`,
          dryRun
        )
        const tokens = ((preparedParams.source as string) ?? '').split('.')
        const datasetname = tokens[0]
        const schemaname = tokens[1]
        const tablename = tokens[2]
        const temptable = `${outputTablename}_temp`
        const subquery = dedent`
            DROP TABLE IF EXISTS ${temptable};
            EXECUTE '
              CREATE TABLE ${temptable} AS
                SELECT DISTINCT column_name AS col_alias
                FROM svv_columns
                WHERE table_name ILIKE ''${tablename}'' AND
                  table_schema ILIKE ''${schemaname}'' AND
                  table_catalog ILIKE ''${datasetname}'' AND
                  column_name != ''${column}''
            ';
            EXECUTE '
              SELECT LISTAGG(col_alias, '', '') aliases
              FROM ${temptable};
            ' INTO columns_def;
            IF columns_def IS NULL THEN
              columns_def:= 'CAST(${column} AS ${newtype}) AS ${column}';
            ELSE
              columns_def:= columns_def || ', ' || 'CAST(${column} AS ${newtype}) AS ${column}';
            END IF;
            DROP TABLE IF EXISTS ${outputTablename};
            EXECUTE
              REPLACE(
                'CREATE TABLE ${outputTablename} AS
                ${tempselect}',
                '%s',
                columns_def
              );
            DROP TABLE ${temptable};`
        query = dedent`
            DECLARE
              columns_def TEXT;
            BEGIN
              ${connection.wrapCodeInIfTableExistsBlock(
                dedent(subquery),
                [outputTablename],
                codeType
              )}
            END;`
        break
      }
      case Provider.DatabricksRest: {
        const select = `SELECT /*+ REPARTITION(40) */ * EXCEPT(${column}),
          CAST(${column} AS ${newtype}) AS ${column}
          FROM ${connection.quoteName(preparedParams.source as string)}`

        query = connection.getCreateTempTableQuery(
          outputTablename,
          select,
          codeType,
          null
        )
        break
      }
      default:
        return null
    }
    return {
      query,
      outputs: { result: outputTablename }
    }
  }

  getUIProperties(provider?: Provider): Record<string, unknown> {
    return getDBTypesByProvider(provider)
  }

  getVersion(provider: Provider): string {
    return '2'
  }

  canProvideOutputSchemas(): boolean {
    return true
  }

  getOutputSchema({
    columns,
    params,
    connection
  }: GetOutputSchemaParams): TableSchema {
    return addColumn(removeColumn(columns.source, params.column as string), {
      name: params.column as string,
      type: connection.dataProviderTypeToColumnType(params.newtype as string)
    })
  }
}

export { Cast }
